import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import useAnalyticsStore from '../stores/analyticsStore';
import useAnalyticsLiveStream from '../hooks/useAnalyticsLiveStream';
import AnalyticsHistogram from './AnalyticsHistogram';
import AnalyticsFilterPanel from './AnalyticsFilterPanel';
import AnalyticsEventList from './AnalyticsEventList';
import AnalyticsEventDetail from './AnalyticsEventDetail';
import AnalyticsLiveToggle from './AnalyticsLiveToggle';

// Embedded per-camera analytics surface (868jww2aq). Reuses the global analytics
// components unchanged, scoping them to one camera via the singleton store's
// lockedCameraId. The global page and this section never co-render (separate
// routes), and each clear()s on mount, so the shared store is safe.
//
// Readiness gate: the children render ONLY once the section effect has
// established scope (lockedCameraId === cameraId). The FilterPanel and Histogram
// self-fetch on their own mount (facets, aggregate), so gating their mount until
// scope is set guarantees those first calls are already camera-scoped: no
// unscoped-then-scoped facet/aggregate race.
//
// Fetch ownership (no duplication): FilterPanel fetches facets, Histogram
// fetches the aggregate. The event list does NOT self-fetch, so the section
// owns fetchEvents() (setFilter already triggers it) plus fetchCameraNames().

const CameraAnalyticsSection = ({ cameraId }) => {
  const clear = useAnalyticsStore((s) => s.clear);
  const setFilter = useAnalyticsStore((s) => s.setFilter);
  const setLockedCameraId = useAnalyticsStore((s) => s.setLockedCameraId);
  const fetchCameraNames = useAnalyticsStore((s) => s.fetchCameraNames);
  const lockedCameraId = useAnalyticsStore((s) => s.lockedCameraId);

  // Re-scope whenever the camera changes. Establish the lock + filter BEFORE any
  // fetch so every read (incl. the children's self-fetches, which only run once
  // the gate below opens) is camera-scoped. setFilter('cameraId', ...) already
  // triggers a scoped fetchEvents; fetchCameraNames is the only other read the
  // section owns (facets + aggregate belong to the gated children).
  useEffect(() => {
    if (!cameraId) return;
    clear();
    setLockedCameraId(cameraId);
    setFilter('cameraId', cameraId);
    fetchCameraNames();
  }, [cameraId, clear, setFilter, setLockedCameraId, fetchCameraNames]);

  // Live mode ([6.7]); default OFF. Scoped to THIS camera so the WS only streams
  // its events. Hook is called unconditionally (before the gate) per hook rules.
  const [live, setLive] = useState(false);
  useAnalyticsLiveStream(live, { cameraId });

  // Gate: hold the children until scope is established, so their mount-fetches
  // are scoped from the first call.
  if (lockedCameraId !== cameraId) {
    return (
      <div className="analytics-embedded">
        <div className="analytics-loading">
          <Loader2 size={18} className="analytics-spin" />
          <span>Loading analytics...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-embedded">
      <div className="analytics-embedded-bar">
        <AnalyticsLiveToggle live={live} onToggle={setLive} />
      </div>
      <AnalyticsHistogram />
      <AnalyticsFilterPanel />
      <AnalyticsEventList />
      <AnalyticsEventDetail />
    </div>
  );
};

export default CameraAnalyticsSection;
